import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Page, Card, Text, Badge, Button, EmptyState, Modal, Banner,
  InlineStack, Toast, Spinner, TextField, Select, Box, Checkbox,
  Popover, ActionList, Tag, Divider
} from '@shopify/polaris';
import {
  PlusIcon, EditIcon, DeleteIcon, SearchIcon, MenuHorizontalIcon
} from '@shopify/polaris-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../auth';
import { useI18n, tp } from '../i18n';
import { PLAN_TYPE_CONFIG, PLAN_TYPES } from '../constants';

const SORT_OPTIONS = (t) => [
  { label: t('sort.date'), value: 'date' },
  { label: t('sort.name'), value: 'name' },
  { label: t('sort.sortOrder'), value: 'sortOrder' },
  { label: t('sort.price'), value: 'price' }
];

function StatusToggle({ checked, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
      style={{
        width: '36px',
        height: '20px',
        borderRadius: '10px',
        border: 'none',
        padding: '2px',
        cursor: 'pointer',
        background: checked ? '#1A1A1A' : '#e3e3e3',
        position: 'relative',
        transition: 'background 0.15s ease',
        flexShrink: 0
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'block',
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          background: '#ffffff',
          boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
          marginLeft: checked ? '16px' : '0',
          transition: 'margin 0.15s ease'
        }}
      />
    </button>
  );
}

const ROW_GRID = {
  display: 'grid',
  gridTemplateColumns: '28px minmax(160px, 1.6fr) minmax(86px, 0.9fr) minmax(86px, 0.9fr) minmax(72px, 0.7fr) auto auto auto',
  gap: '12px',
  alignItems: 'center'
};

export default function PlansList() {
  const { t } = useI18n();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState('date');
  const [statusFilters, setStatusFilters] = useState([]); // ['active', 'inactive']
  const [typeFilters, setTypeFilters] = useState([]); // plan type values
  const [filterOpen, setFilterOpen] = useState(false);
  const [menuPlanId, setMenuPlanId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deletingPlan, setDeletingPlan] = useState(null);
  const [seedingDemo, setSeedingDemo] = useState(false);
  const [toast, setToast] = useState(null);
  const [pageError, setPageError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { getPlans, deletePlan, updatePlan, seedData } = useApi();
  const { getSessionToken } = useAuth();
  const embedded = Boolean(getSessionToken);

  // Cross-page feedback (e.g. "Plan saved" after returning from its settings page)
  useEffect(() => {
    if (location.state?.toast) {
      setToast({ content: location.state.toast });
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      // No auto-seed here: an empty list shows the empty state. Re-seeding on
      // empty used to resurrect demo plans over the merchant's own data.
      setPlans(await getPlans());
    } catch (err) {
      setPageError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getPlans]);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  // Explicit opt-in: replaces the shop's plans with the three demo plans
  // (backend requires ?replace=true to touch existing data).
  const loadDemoPlans = useCallback(async () => {
    setSeedingDemo(true);
    try {
      await seedData(true);
      await loadPlans();
      setToast({ content: t('plans.demoLoaded') });
    } catch (err) {
      setToast({ content: t('plans.demoFailed', { message: err.message }) });
    } finally {
      setSeedingDemo(false);
    }
  }, [seedData, loadPlans]);

  const toggleActive = useCallback(async (plan, nextActive) => {
    // Optimistic flip; revert on failure.
    setPlans(prev => prev.map(p => (p.id === plan.id ? { ...p, isActive: nextActive } : p)));
    try {
      await updatePlan(plan.id, { isActive: nextActive });
      setToast({ content: nextActive ? t('plans.activated') : t('plans.deactivated') });
    } catch (err) {
      setPlans(prev => prev.map(p => (p.id === plan.id ? { ...p, isActive: plan.isActive } : p)));
      setToast({ content: t('plans.updateFailed', { message: err.message }) });
    }
  }, [updatePlan]);

  const confirmDelete = useCallback(async () => {
    if (!deletingPlan) return;
    try {
      await deletePlan(deletingPlan.id);
      setPlans(prev => prev.filter(p => p.id !== deletingPlan.id));
      setSelectedIds(prev => { const n = new Set(prev); n.delete(deletingPlan.id); return n; });
      setToast({ content: t('plans.deleted') });
    } catch (err) {
      setToast({ content: t('plans.deleteFailed', { message: err.message }) });
    } finally {
      setDeletingPlan(null);
    }
  }, [deletingPlan, deletePlan]);

  const applyBulk = useCallback(async (ids, action) => {
    if (action === 'delete') {
      try {
        await Promise.all(ids.map(id => deletePlan(id)));
        setPlans(prev => prev.filter(p => !ids.includes(p.id)));
        setToast({ content: tp('plans.bulkDeleted', ids.length) });
      } catch (err) {
        setToast({ content: t('plans.deleteBulkFailed', { message: err.message }) });
        await loadPlans();
      }
    } else {
      const nextActive = action === 'activate';
      setPlans(prev => prev.map(p => (ids.includes(p.id) ? { ...p, isActive: nextActive } : p)));
      try {
        await Promise.all(ids.map(id => updatePlan(id, { isActive: nextActive })));
        setToast({ content: tp(nextActive ? 'plans.bulkActivated' : 'plans.bulkDeactivated', ids.length) });
      } catch (err) {
        setToast({ content: t('plans.updateBulkFailed', { message: err.message }) });
        await loadPlans();
      }
    }
    setSelectedIds(new Set());
  }, [deletePlan, updatePlan, loadPlans]);

  const visiblePlans = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = plans.filter(p => {
      if (q && !(p.name || '').toLowerCase().includes(q) &&
          !(p.displayName || '').toLowerCase().includes(q)) return false;
      if (statusFilters.length === 1) {
        if (statusFilters.includes('active') && !p.isActive) return false;
        if (statusFilters.includes('inactive') && p.isActive) return false;
      }
      if (typeFilters.length > 0 && !typeFilters.includes(p.type)) return false;
      return true;
    });
    const sorted = [...filtered];
    switch (sortKey) {
      case 'name':
        sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'sortOrder':
        sorted.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        break;
      case 'price':
        sorted.sort((a, b) => (a.priceValue || 0) - (b.priceValue || 0));
        break;
      default:
        sorted.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    }
    return sorted;
  }, [plans, query, statusFilters, typeFilters, sortKey]);

  const toggleSelect = (id, checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const allVisibleSelected = visiblePlans.length > 0 &&
    visiblePlans.every(p => selectedIds.has(p.id));
  const someVisibleSelected = visiblePlans.some(p => selectedIds.has(p.id));

  const toggleSelectAll = (checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      visiblePlans.forEach(p => (checked ? next.add(p.id) : next.delete(p.id)));
      return next;
    });
  };

  // Handler factory for the filter checkboxes/tags. Returns a stable callback
  // that Polaris invokes on toggle; must never run during render.
  const toggleListFilter = (setter, value) => () => {
    setter(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));
  };

  const filtersApplied = statusFilters.length > 0 || typeFilters.length > 0;

  const renderRow = (plan) => {
    const typeConfig = PLAN_TYPE_CONFIG[plan.type] || PLAN_TYPE_CONFIG.custom;
    const typeKey = PLAN_TYPE_CONFIG[plan.type] ? plan.type : 'custom';
    const selected = selectedIds.has(plan.id);    return (
      <div
        key={plan.id}
        className="pp-plan-row"
        onClick={() => navigate(`/plans/${plan.id}`)}
        style={{ ...ROW_GRID, padding: '9px 16px', cursor: 'pointer' }}
      >
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            label=""
            labelHidden
            checked={selected}
            onChange={(value) => toggleSelect(plan.id, value)}
          />
        </div>
        <div style={{ minWidth: 0 }}>
          <Text variant="bodyMd" fontWeight="bold" as="p" truncate>
            {plan.displayName || plan.name}
          </Text>
        </div>
        <Text variant="bodySm" as="p" tone="subdued" truncate>{t(`planTypeShort.${typeKey}`)}</Text>
        <Text variant="bodySm" as="p" tone="subdued">{t('plans.onlineStore')}</Text>
        <Text variant="bodySm" as="p" tone="subdued">
          {plan.isOptional ? t('plans.optional') : t('plans.automatic')}
        </Text>
        <div onClick={(e) => e.stopPropagation()}>
          <Popover
            active={menuPlanId === plan.id}
            activator={
              <Button
                icon={MenuHorizontalIcon}
                accessibilityLabel={t('plans.actionsFor', { name: plan.name })}
                onClick={() => setMenuPlanId(menuPlanId === plan.id ? null : plan.id)}
              />
            }
            onClose={() => setMenuPlanId(null)}
          >
            <ActionList
              items={[
                {
                  content: t('common.edit'),
                  icon: EditIcon,
                  onAction: () => { setMenuPlanId(null); navigate(`/plans/${plan.id}`); }
                },
                {
                  content: t('common.delete'),
                  icon: DeleteIcon,
                  destructive: true,
                  onAction: () => { setMenuPlanId(null); setDeletingPlan(plan); }
                }
              ]}
            />
          </Popover>
        </div>
        {plan.isActive
          ? <Badge tone="success">{t('plans.statusActive')}</Badge>
          : <Badge tone="critical">{t('plans.statusInactive')}</Badge>}
        <div onClick={(e) => e.stopPropagation()}>
          <StatusToggle
            checked={plan.isActive}
            onChange={(next) => toggleActive(plan, next)}
            label={plan.isActive ? t('plans.deactivatePlan', { name: plan.name }) : t('plans.activatePlan', { name: plan.name })}
          />
        </div>
      </div>
    );
  };

  const listBody = loading ? (
    <div style={{ padding: '64px', textAlign: 'center' }}>
      <Spinner accessibilityLabel={t('plans.loading')} size="large" />
    </div>
  ) : plans.length === 0 ? (
    <EmptyState
      heading={t('plans.empty.heading')}
      action={{ content: t('common.createPlan'), onAction: () => navigate('/plans/new') }}
      secondaryAction={embedded ? undefined : {
        content: t('plans.loadDemo'),
        loading: seedingDemo,
        onAction: loadDemoPlans
      }}
      image="https://cdn.shopify.com/s/files/1/0262/4071/1926/files/emptystate-files.png"
    >
      <p>
        {t('plans.empty.body')}
      </p>
    </EmptyState>
  ) : (
    <>
      <Box padding="400" paddingBlockEnd="300">
        <TextField
          label=""
          labelHidden
          placeholder={t('plans.filterPlaceholder')}
          value={query}
          onChange={setQuery}
          autoComplete="off"
          prefix={<SearchIcon />}
          clearButton
          onClear={() => setQuery('')}
        />
        <Box paddingBlockStart="200">
          <Popover
            active={filterOpen}
            activator={
              <Button icon={PlusIcon} onClick={() => setFilterOpen(o => !o)}>
                {t('plans.addFilter')}
              </Button>
            }
            onClose={() => setFilterOpen(false)}
            autofocusTarget="none"
          >
            <Popover.Pane>
              <Box padding="400" minWidth="230px">
                <Text variant="headingXs" as="h3">{t('plans.statusHeading')}</Text>
                <Box paddingBlockStart="200">
                  <Checkbox
                    label={t('plans.statusActive')}
                    checked={statusFilters.includes('active')}
                    onChange={toggleListFilter(setStatusFilters, 'active')}
                  />
                  <Checkbox
                    label={t('plans.statusInactive')}
                    checked={statusFilters.includes('inactive')}
                    onChange={toggleListFilter(setStatusFilters, 'inactive')}
                  />
                </Box>
                <Box paddingBlockStart="300">
                  <Text variant="headingXs" as="h3">{t('plans.typeHeading')}</Text>
                </Box>
                <Box paddingBlockStart="200">
                  {PLAN_TYPES.map(type => (
                    <Checkbox
                      key={type.value}
                      label={t(`planType.${type.value}`)}
                      checked={typeFilters.includes(type.value)}
                      onChange={toggleListFilter(setTypeFilters, type.value)}
                    />
                  ))}
                </Box>
              </Box>
            </Popover.Pane>
          </Popover>
        </Box>
        {filtersApplied && (
          <Box paddingBlockStart="200">
            <InlineStack gap="200" wrap>
              {statusFilters.map(f => (
                <Tag key={f} onDismiss={toggleListFilter(setStatusFilters, f)}>
                  {f === 'active' ? t('plans.statusActive') : t('plans.statusInactive')}
                </Tag>
              ))}
              {typeFilters.map(typeValue => (
                <Tag key={typeValue} onDismiss={toggleListFilter(setTypeFilters, typeValue)}>
                  {t(`planTypeShort.${PLAN_TYPE_CONFIG[typeValue] ? typeValue : 'custom'}`)}
                </Tag>
              ))}
            </InlineStack>
          </Box>
        )}
      </Box>

      <Divider />

      <Box padding="300" paddingBlockEnd="150">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              label=""
              labelHidden
              checked={allVisibleSelected}
              indeterminate={!allVisibleSelected && someVisibleSelected}
              onChange={toggleSelectAll}
              accessibilityLabel={t('plans.selectAll')}
            />
          </div>
          <Text variant="bodyMd" as="p">
            {tp('plans.showing', visiblePlans.length)}
          </Text>
          <div style={{ flex: 1 }} />
          <InlineStack gap="200" blockAlign="center">
            <Text variant="bodyMd" as="span" tone="subdued">{t('plans.sortBy')}</Text>
            <div style={{ width: '190px' }}>
              <Select
                label=""
                labelHidden
                options={SORT_OPTIONS(t)}
                value={sortKey}
                onChange={setSortKey}
              />
            </div>
          </InlineStack>
        </div>
      </Box>

      {selectedIds.size > 0 && (
        <Box padding="300" paddingBlockStart="0">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              background: '#f1f1f1',
              borderRadius: '8px',
              padding: '8px 12px'
            }}
          >
            <Text variant="bodySm" as="p" fontWeight="medium">
              {t('plans.selectedCount', { count: selectedIds.size })}
            </Text>
            <Button size="slim" onClick={() => applyBulk([...selectedIds], 'activate')}>
              {t('common.activate')}
            </Button>
            <Button size="slim" onClick={() => applyBulk([...selectedIds], 'deactivate')}>
              {t('common.deactivate')}
            </Button>
            <Button
              size="slim"
              destructive
              onClick={() => applyBulk([...selectedIds], 'delete')}
            >
              {t('common.delete')}
            </Button>
          </div>
        </Box>
      )}

      {visiblePlans.length > 0 ? (
        /* 窄屏行内最小宽度超过视口时可以在卡片内横滑,不撑破整页布局 */
        <div style={{ overflowX: 'auto' }}>{visiblePlans.map(renderRow)}</div>
      ) : (
        <Box padding="400">
          <Text variant="bodyMd" as="p" tone="subdued" alignment="center">
            {t('plans.noMatch')}
          </Text>
        </Box>
      )}
    </>
  );

  return (
    <Page
      title={t('plans.title')}
      primaryAction={{ content: t('common.createPlan'), icon: PlusIcon, onAction: () => navigate('/plans/new') }}
    >
      <style>{`
        .pp-plan-row:hover { background: #f7f7f8; }
        .pp-plan-row:not(:last-child) { border-bottom: 1px solid #f1f1f1; }
        /* 窄屏:类型/销售渠道两列信息价值最低,直接隐藏让行宽贴近视口;
           剩余单元格按 grid 自动重排到 6 列模板上(选择框/名称/添加方式/
           菜单/状态/开关),仍放不下时由外层横向滚动兜底。 */
        @media (max-width: 767px) {
          .pp-plan-row { grid-template-columns: 28px minmax(140px, 1.6fr) minmax(86px, 0.9fr) auto auto auto !important; }
          .pp-plan-row > :nth-child(3), .pp-plan-row > :nth-child(4) { display: none; }
        }
      `}</style>
      <div style={{ maxWidth: '960px', margin: '0 auto' }}>
        {pageError && (
          <Banner
            tone="critical"
            title={t('plans.errorTitle')}
            action={{ content: t('common.retry'), onAction: loadPlans }}
            onDismiss={() => setPageError(null)}
          >
            <p>{pageError}</p>
          </Banner>
        )}

        <Card>{listBody}</Card>

        {!loading && plans.length > 0 && (
          <Box paddingBlockStart="400">
            <Text variant="bodyMd" as="p" alignment="center">
              {t('plans.helpPrefix')}
              <Text variant="bodyMd" as="span" fontWeight="medium">
                {t('plans.helpLink')}
              </Text>
              {t('plans.helpSuffix')}
            </Text>
          </Box>
        )}
      </div>

      <Modal
        open={Boolean(deletingPlan)}
        onClose={() => setDeletingPlan(null)}
        title={t('plans.deleteTitle')}
        primaryAction={{ content: t('common.delete'), destructive: true, onAction: confirmDelete }}
        secondaryActions={[{ content: t('common.cancel'), onAction: () => setDeletingPlan(null) }]}
      >
        <Modal.Section>
          <Text variant="bodyMd" as="p">
            <strong>{deletingPlan?.name}</strong> {t('plans.deleteBody')}
          </Text>
        </Modal.Section>
      </Modal>

      {toast && <Toast content={toast.content} onDismiss={() => setToast(null)} />}
    </Page>
  );
}
